import { morphSection } from '@theme/section-renderer';

const ZIPFLASH_PRODUCT_ID = '15422994907501';
const WELCOME_CODES = new Set(['WELCOME2', 'WELCOME10']);
const cartRoot = () => window.Shopify?.routes?.root || '/';

function welcomeCode(cart, productId) {
  const hasZipFlash = cart ? cart.items?.some(item => String(item.product_id) === ZIPFLASH_PRODUCT_ID) : String(productId) === ZIPFLASH_PRODUCT_ID;
  return hasZipFlash ? 'WELCOME10' : 'WELCOME2';
}

const BUNDLE_CODE_BASES = {"15086808629613": "COOLCLIP", "15422984913261": "COZYSTITCH", "15422994907501": "ZIPFLASH", "15423000510829": "PURRCLEAN", "15423001526637": "PETHUG", "15423002116461": "WHALESIP", "15423002476909": "LIGHTPAWS", "15423003263341": "AQUAGROOM", "15423004082541": "PAWHAMMOCK", "15423007850861": "PAWTREE", "15423013519725": "FLAPPING", "15423016862061": "OCTOPUS", "15423017877869": "WITCH", "15423018434925": "CANOPY", "15423019647341": "WOOD", "15423021384045": "JOYFY", "15423021842797": "RETRIEVER", "15423022236013": "HAMMOCK", "15423097897325": "FARMFUN", "15423024922989": "DOG", "15423026266477": "PLUSH", "15423026954605": "CACTUS", "15424228688237": "ECOBLOSSOM"};
const BUNDLE_CODES = new Set(Object.values(BUNDLE_CODE_BASES).flatMap(base => [`${base}2`, `${base}3`]));

function bundleCodes(cart) {
  if (!cart?.items) return [];
  const quantities = new Map();
  for (const item of cart.items) {
    const base = BUNDLE_CODE_BASES[String(item.product_id)];
    if (!base) continue;
    quantities.set(base, (quantities.get(base) || 0) + (item.quantity || 0));
  }
  const result = [];
  for (const [base, qty] of quantities) {
    if (qty >= 3) result.push(`${base}3`);
    else if (qty === 2) result.push(`${base}2`);
  }
  return result;
}

function welcomeCodes(cart, productId) {
  const codes = cart ? (cart.discount_codes || []).map(discount => discount.code) : [...document.querySelectorAll('[data-discount-code]')].map(node => node.dataset.discountCode);
  const customerCodes = codes.filter(Boolean).map(code => code.toUpperCase()).filter(code => !WELCOME_CODES.has(code) && !BUNDLE_CODES.has(code));
  return [...new Set([...customerCodes, welcomeCode(cart, productId), ...bundleCodes(cart)])];
}

export function welcomeShopCheckoutUrl(productId) {
  const url = new URL(document.baseURI);
  url.pathname = '/';
  url.search = '';
  url.hash = '';
  url.searchParams.set('discount', welcomeCodes(undefined, productId).join(','));
  return url.href;
}

/** Shopify calculates eligibility and totals; a failed promotion never blocks adding an item. */
export async function applyWelcomeGift(cart, sectionIds = []) {
  if (!cart?.item_count) return cart;
  const expected = welcomeCodes(cart);
  const current = (cart.discount_codes || []).map(discount => discount.code.toUpperCase());
  if (expected.length === current.length && expected.every(code => current.includes(code))) return cart;
  try {
    const response = await fetch(cartRoot() + 'cart/update.js', {
      method: 'POST', headers: {'Content-Type':'application/json', Accept:'application/json'},
      body: JSON.stringify({discount: welcomeCodes(cart).join(','), sections: [...new Set(sectionIds)].slice(0, 5), sections_url: new URL(document.baseURI).pathname})
    });
    if (!response.ok) return cart;
    const updated = await response.json();
    return updated.items ? updated : cart;
  } catch { return cart; }
}

/** Animate only after Shopify confirms the add, before the drawer opens. */
export async function flyProductToCart(source, imageUrl) {
  if (!source || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const target = document.querySelector('.header-actions__cart-icon');
  const sticky = source.closest('gsg-purchase-bar');
  const product = source.closest('product-card, product-form-component, [id^="shopify-section-"]');
  const image = sticky?.querySelector('img') || product?.querySelector('media-gallery img, .product-card__image, img');
  const src = imageUrl || image?.currentSrc || image?.src;
  if (!target || !src) return;
  const start = source.getBoundingClientRect();
  const end = target.getBoundingClientRect();
  if (!start.width || !start.height) return;
  const size = 68;
  const x = Math.max(8, Math.min(innerWidth - size - 8, start.left + start.width / 2 - size / 2));
  const y = Math.max(8, Math.min(innerHeight - size - 8, start.top + start.height / 2 - size / 2));
  const dx = Math.max(18, Math.min(innerWidth - 18, end.left + end.width / 2)) - x - size / 2;
  const dy = Math.max(22, Math.min(innerHeight - 22, end.top + end.height / 2)) - y - size / 2;
  const clone = document.createElement('img');
  clone.className = 'gsg-flying-product';
  clone.src = src;
  clone.alt = '';
  clone.setAttribute('aria-hidden', 'true');
  Object.assign(clone.style, {left:`${x}px`, top:`${y}px`});
  document.body.append(clone);
  try {
    await clone.animate([
      {transform:'translate(0, 0) scale(1)', opacity:1},
      {transform:`translate(${dx * .38}px, ${dy * .55 - 44}px) scale(.85)`, opacity:1, offset:.45},
      {transform:`translate(${dx}px, ${dy}px) scale(.16)`, opacity:.15}
    ], {duration:520, easing:'cubic-bezier(.4,0,.2,1)'}).finished;
    target.animate([{transform:'scale(1)'},{transform:'scale(1.16)'},{transform:'scale(1)'}], {duration:220});
  } catch { /* Navigation and reduced-motion changes can cancel an animation. */ }
  finally { clone.remove(); }
}

// Covers an existing cart opened through a direct cart link. Product forms also
// await applyWelcomeGift before rendering their successful add response.
async function initializeGift() {
  const perks = document.querySelector('[data-gsg-cart-perks]');
  if (!perks || perks.dataset.welcomeApplied === 'true') return;
  try {
    const response = await fetch(cartRoot() + 'cart.js', {headers:{Accept:'application/json'}});
    if (!response.ok) return;
    const cart = await response.json();
    const sections = [...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId);
    const updated = await applyWelcomeGift(cart, sections);
    for (const [id, html] of Object.entries(updated.sections || {})) {
      if (typeof html === 'string') morphSection(id, html, {mode:document.querySelector('theme-drawer#cart-drawer[open]') ? 'hydration' : 'full'});
    }
  } catch { /* The code remains available to enter at checkout. */ }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeGift, {once:true});
else initializeGift();

/* GSG: keep at most 2 express checkout buttons in the cart drawer (Oct 22) */
function gsgButtonItems(root) {
  const isBtn = (el) => el.tagName === 'BUTTON' || el.tagName === 'IFRAME' || (el.querySelector && el.querySelector('button, iframe'));
  let list = root.querySelector('ul') || root;
  for (let depth = 0; depth < 4; depth++) {
    const kids = Array.from(list.children || []).filter((el) => el.nodeType === 1 && el.tagName !== 'STYLE');
    const bearing = kids.filter(isBtn);
    if (bearing.length !== 1) return bearing;
    const inner = Array.from(bearing[0].children || []).filter((el) => el.nodeType === 1 && el.tagName !== 'STYLE');
    if (inner.length < 2) return bearing;
    list = bearing[0];
  }
  return Array.from(list.children || []).filter((el) => el.nodeType === 1 && el.tagName !== 'STYLE');
}
function gsgTrimExpressButtons() {
  try {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    const containers = [];
    const lightBox = drawer.querySelector('.additional-checkout-buttons');
    if (lightBox) containers.push(lightBox);
    const host = drawer.querySelector('shopify-accelerated-checkout');
    if (host && host.shadowRoot) containers.push(host.shadowRoot);
    containers.forEach((box) => {
      gsgButtonItems(box).forEach((el, i) => {
        if (i >= 2) el.style.setProperty('display', 'none', 'important');
      });
    });
  } catch (e) { /* leave default rendering untouched */ }
}
let gsgTrimTimer;
function gsgScheduleTrim() { clearTimeout(gsgTrimTimer); gsgTrimTimer = setTimeout(gsgTrimExpressButtons, 80); }
if (typeof MutationObserver !== 'undefined') {
  const gsgObs = new MutationObserver(gsgScheduleTrim);
  const gsgStartObs = () => {
    const d = document.getElementById('cart-drawer');
    if (d) { gsgObs.observe(d, { childList: true, subtree: true }); gsgTrimExpressButtons(); }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', gsgStartObs); else gsgStartObs();
}
[400, 1200, 2500, 4500].forEach((ms) => setTimeout(gsgTrimExpressButtons, ms));
document.addEventListener('click', () => setTimeout(gsgTrimExpressButtons, 400), true);
